import React, { useMemo, useRef, useCallback } from 'react';
import { AthleteResult } from "../domain/AthleteDetail";
import { AthleteDetailWithStandard } from "../domain/AthleteDetailWithStandard";
import { StandardsTableProps } from "./../domain/StandardsTableProps";
import { convertTimeToMs, getCompetitionName, getTranslatedCompetitionName, isStandardColumn, GetFormatedTime, GetFormatedTimeDiff } from "./StandardsUtils";
import { ClickAwayListener, LinearProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-material.css';
import './StandardsTableAgGridOverrides.css';
import type { ColDef } from 'ag-grid-community';

const NormalCellRenderer = (params: any) => <p> {params.value} </p>;


// AG Grid cell renderer for standards columns, with MUI Tooltip and formatting
const StandardsAgCellRenderer = (props: any) => {
    const { value, data, colDef } = props;
    const [open, setOpen] = React.useState(false);
    const theme = useTheme();

    // Compute average per 50m for tooltip if possible
    let averagePer50M: string | null = null;
    const competition = data?.Competition;
    if (value && competition) {
        const timeMs = convertTimeToMs(value);
        const distanceMatches = competition.match(/\d+/g);
        if (distanceMatches) {
            const distance = parseInt(distanceMatches[0], 10);
            if (!isNaN(distance) && distance > 50) {
                const avg = timeMs / (distance / 50);
                averagePer50M = GetFormatedTime(avg);
            }
        }
    }

    // Time diff for standards columns
    let timeDiff = "";
    if (isStandardColumn(colDef.field) && data?.Time && value) {
        const time = convertTimeToMs(data.Time);
        const standard = convertTimeToMs(value);
        
        timeDiff = GetFormatedTimeDiff(time, standard);
    }

    return (
        <ClickAwayListener touchEvent="onTouchStart" onClickAway={() => setOpen(false)}>
            <Tooltip
                open={open}
                onClose={() => setOpen(false)}
                title={averagePer50M ? (
                    <>
                        <Typography sx={{ fontSize: '0.8em' }}>Moy./50m</Typography>
                        <Typography sx={{ fontSize: '0.8em' }}>{averagePer50M}</Typography>
                    </>
                ) : ""}
                arrow
                PopperProps={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 1],
                            },
                        },
                    ],
                }}
            >

                <Stack onClick={() => setOpen(true)} height={"100%"} direction="column" justifyContent={"center"}>
                    {value}
                    {isStandardColumn(colDef.field) && <div style={{ fontSize: 10, color: theme.palette.text.secondary }}>{timeDiff}</div>}
                </Stack>

            </Tooltip>
        </ClickAwayListener>
    );
};

const getColumns = (show: any): ColDef<AthleteDetailWithStandard>[] => [
    { field: 'Competition' as keyof AthleteDetailWithStandard, headerName: 'Épreuve', width: 100, sortable: false, resizable: false, suppressMovable:true, pinned: true, lockPinned: true, cellRenderer: NormalCellRenderer },
    { field: 'Course' as keyof AthleteDetailWithStandard, headerName: 'Bassin', width: 60, sortable: false, resizable: false, suppressMovable:true, hide: !show.Course },
    { field: 'Time' as keyof AthleteDetailWithStandard, headerName: 'Temps', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.Time, cellRenderer: StandardsAgCellRenderer },
    { field: 'Open' as keyof AthleteDetailWithStandard, headerName: 'Ouvert', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.Open, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A11' as keyof AthleteDetailWithStandard, headerName: '11 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A11, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A12' as keyof AthleteDetailWithStandard, headerName: '12 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A12, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A13' as keyof AthleteDetailWithStandard, headerName: '13 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A13, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A14' as keyof AthleteDetailWithStandard, headerName: '14 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A14, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A15' as keyof AthleteDetailWithStandard, headerName: '15 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A15, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A16' as keyof AthleteDetailWithStandard, headerName: '16 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A16, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A17' as keyof AthleteDetailWithStandard, headerName: '17 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A17, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'A18' as keyof AthleteDetailWithStandard, headerName: '18 ans', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.A18, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'Junior' as keyof AthleteDetailWithStandard, headerName: 'Junior', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.Junior, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'Senior' as keyof AthleteDetailWithStandard, headerName: 'Senior', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.Senior, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'UsportSCM' as keyof AthleteDetailWithStandard, headerName: 'Usport', width: 90, sortable: false, resizable: false, suppressMovable:true, hide: !show.UsportSCM, cellRenderer: StandardsAgCellRenderer, cellClass: 'ag-center-cols-container' },
    { field: 'Date' as keyof AthleteDetailWithStandard, headerName: 'Date', width: 100, sortable: false, resizable: false, suppressMovable:true, hide: !show.Date, cellRenderer: NormalCellRenderer },
];

const getAthleteTimeForCompetition = (athleteDetails: AthleteResult[], competition: string, course: string): { time: string, date: string } => {
console.log(athleteDetails);
    // TODO Replace toUpperCase with localeCompare?
    const time = athleteDetails.filter(a => a.competition.toUpperCase() === competition.toUpperCase())
        .filter(a => a.course.toUpperCase() === course.toUpperCase());

    if (time[0]) {
        return { time: time[0].time, date: time[0].date };
    }

    return { time: "", date: "" };
};

export default function StandardsTable(props: StandardsTableProps): JSX.Element {
    const { standards, athleteResults, loading } = props;
    const gridRef = useRef<any>(null);

    const getTimesWithStandards = useMemo(() => {
        let i = 1;
        return standards.map((x: any) => {
            const athleteDetailWithStandards = new AthleteDetailWithStandard();
            const competitionName = getCompetitionName(x.Competition, x.Style);
            const { time, date } = athleteResults ? getAthleteTimeForCompetition(athleteResults, getTranslatedCompetitionName(competitionName), x.Course) : { time: "", date: "" };
            athleteDetailWithStandards.id = i++;
            athleteDetailWithStandards.Competition = competitionName;
            athleteDetailWithStandards.Standard = x.std_name;
            athleteDetailWithStandards.Course = x.Course;
            athleteDetailWithStandards.Time = time;
            athleteDetailWithStandards.Date = date;
            athleteDetailWithStandards.Open = x.Open;
            athleteDetailWithStandards.A11 = x.A11;
            athleteDetailWithStandards.A12 = x.A12;
            athleteDetailWithStandards.A13 = x.A13;
            athleteDetailWithStandards.A14 = x.A14;
            athleteDetailWithStandards.A15 = x.A15;
            athleteDetailWithStandards.A16 = x.A16;
            athleteDetailWithStandards.A17 = x.A17;
            athleteDetailWithStandards.A18 = x.A18;
            athleteDetailWithStandards.Junior = x.Junior;
            athleteDetailWithStandards.Senior = x.Senior;
            athleteDetailWithStandards.UsportSCM = x.UsportSCM;
            return athleteDetailWithStandards;
        });
    }, [standards, athleteResults]);

    function isStandardObtained(standard: string, time: string) {
        const standardInMs: number = convertTimeToMs(standard);
        const timeInMs: number = convertTimeToMs(time);
        return timeInMs <= standardInMs;
    }

    const showA11Column = standards.length > 0 && standards[0].A11 !== undefined;
    const showA12Column = standards.length > 0 && standards[0].A12 !== undefined;
    const showA13Column = standards.length > 0 && standards[0].A13 !== undefined;
    const showA14Column = standards.length > 0 && standards[0].A14 !== undefined;
    const showA15Column = standards.length > 0 && standards[0].A15 !== undefined;
    const showA16Column = standards.length > 0 && standards[0].A16 !== undefined;
    const showA17Column = standards.length > 0 && standards[0].A17 !== undefined;
    const showA18Column = standards.length > 0 && standards[0].A18 !== undefined;
    const showOpenColumn = standards.length > 0 && standards[0].Open !== undefined;
    const showJuniorColumn = standards.length > 0 && standards[0].Junior !== undefined;
    const showSeniorColumn = standards.length > 0 && standards[0].Senior !== undefined;
    const showUsportSCMColumn = standards.length > 0 && standards[0].UsportSCM !== undefined;
    const showDateAndTime = athleteResults ? athleteResults.length > 0 : false;

    const agColumns = useMemo(() => getColumns({
        Course: false,
        Date: showDateAndTime,
        Time: showDateAndTime,
        A11: showA11Column,
        A12: showA12Column,
        A13: showA13Column,
        A14: showA14Column,
        A15: showA15Column,
        A16: showA16Column,
        A17: showA17Column,
        A18: showA18Column,
        Open: showOpenColumn,
        Junior: showJuniorColumn,
        Senior: showSeniorColumn,
        UsportSCM: showUsportSCMColumn,
    }), [showDateAndTime, showA11Column, showA12Column, showA13Column, showA14Column, showA15Column, showA16Column, showA17Column, showA18Column, showOpenColumn, showJuniorColumn, showSeniorColumn, showUsportSCMColumn]);

    const cellClassRules = {
        'green': (params: any) => {
            if (isStandardColumn(params.colDef.field) && params.data && params.value && params.data.Time) {
                return isStandardObtained(params.value, params.data.Time);
            }
            return false;
        }
    };

    const getRowClass = useCallback((params: any) => {
        return 'default-row';
    }, []);

    return (
        <div style={{ position: 'relative' }}>
        {loading && <LinearProgress style={{ position: 'absolute', top: 45, left: 0, right: 0, zIndex: 1 }} />}
        <div className="ag-theme-material" style={{ minHeight: 400 }}>
            <AgGridReact
                ref={gridRef}
                rowData={getTimesWithStandards}
                columnDefs={agColumns}
                rowHeight={45}
                headerHeight={45}
                maintainColumnOrder={true}
                suppressMenuHide={true}
                suppressColumnVirtualisation={true}
                getRowClass={getRowClass}
                domLayout="autoHeight"
                defaultColDef={{
                    resizable: false,
                    sortable: false,
                    cellClassRules,
                }}
            />
        </div>
        </div>
    );
}